/**
 * ImageDropZone Component
 *
 * Reusable drag-and-drop image upload component.
 * Supports file selection via click or drag-and-drop with validation.
 */

import { useState, useCallback, useRef, type CSSProperties, type DragEvent } from 'react';

/**
 * Props for ImageDropZone component
 */
interface ImageDropZoneProps {
  /** Called when a valid file is selected */
  onFileSelect: (file: File) => void;
  /** Called when the file is removed */
  onRemove?: () => void;
  /** Preview URL for the currently selected image */
  previewUrl?: string | null;
  /** Maximum file size in bytes (default: 5MB) */
  maxSize?: number;
  /** Allowed MIME types (default: common image types) */
  allowedTypes?: string[];
  /** Placeholder text when no file is selected */
  placeholder?: string;
  /** Text shown when dragging over the drop zone */
  dragActiveText?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Called when validation fails */
  onError?: (message: string) => void;
  /** Height of the preview image (default: 200px) */
  previewHeight?: number;
}

/**
 * Default maximum file size (5MB)
 */
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024;

/**
 * Default allowed MIME types
 *
 * SECURITY NOTE: SVG (image/svg+xml) is intentionally excluded because SVG files
 * can contain embedded JavaScript that executes when rendered in a browser,
 * creating XSS vulnerabilities. Only raster image formats are allowed.
 */
const DEFAULT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Safe MIME type to file extension mapping.
 *
 * Use this to derive file extensions from validated MIME types instead of
 * trusting user-provided filenames, which can be spoofed or contain
 * path traversal characters.
 *
 * @example
 * ```typescript
 * const extension = MIME_TO_EXTENSION[file.type] ?? 'png';
 * const storagePath = `uploads/${userId}/${timestamp}.${extension}`;
 * ```
 */
export const MIME_TO_EXTENSION: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

/**
 * ImageDropZone component for drag-and-drop image uploads
 *
 * @example
 * ```tsx
 * const [file, setFile] = useState<File | null>(null);
 * const [preview, setPreview] = useState<string | null>(null);
 *
 * const handleSelect = (file: File) => {
 *   setFile(file);
 *   const reader = new FileReader();
 *   reader.onloadend = () => setPreview(reader.result as string);
 *   reader.readAsDataURL(file);
 * };
 *
 * <ImageDropZone
 *   onFileSelect={handleSelect}
 *   onRemove={() => { setFile(null); setPreview(null); }}
 *   previewUrl={preview}
 * />
 * ```
 */
export const ImageDropZone = ({
  onFileSelect,
  onRemove,
  previewUrl,
  maxSize = DEFAULT_MAX_SIZE,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  placeholder = 'Drag & drop or click to add image',
  dragActiveText = 'Drop image here',
  disabled = false,
  error,
  onError,
  previewHeight = 200,
}: ImageDropZoneProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Validate and process a file
   */
  const processFile = useCallback(
    (file: File) => {
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        const typeList = allowedTypes.map((t) => t.replace('image/', '')).join(', ');
        const message = `File must be one of: ${typeList.toUpperCase()}`;
        onError?.(message);
        return;
      }

      // Validate file size
      if (file.size > maxSize) {
        const sizeMB = Math.round(maxSize / (1024 * 1024));
        const message = `File must be less than ${sizeMB}MB`;
        onError?.(message);
        return;
      }

      onFileSelect(file);
    },
    [allowedTypes, maxSize, onFileSelect, onError]
  );

  /**
   * Handle file input change
   */
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        processFile(file);
      }
      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [processFile]
  );

  /**
   * Handle drag over event
   */
  const handleDragOver = useCallback(
    (event: DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  /**
   * Handle drag leave event
   */
  const handleDragLeave = useCallback((event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  }, []);

  /**
   * Handle drop event
   */
  const handleDrop = useCallback(
    (event: DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const file = event.dataTransfer.files[0];
      if (file) {
        processFile(file);
      }
    },
    [disabled, processFile]
  );

  /**
   * Handle click on the zone
   */
  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  // Show preview if available
  if (previewUrl) {
    return (
      <div style={previewContainerStyle}>
        <img
          src={previewUrl}
          alt="Preview"
          style={{ ...previewImageStyle, maxHeight: previewHeight }}
        />
        {onRemove && (
          <button type="button" onClick={onRemove} style={removeButtonStyle} disabled={disabled}>
            Remove
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <label
        style={{
          ...dropZoneStyle,
          ...(isDragOver ? dropZoneDragOverStyle : {}),
          ...(disabled ? dropZoneDisabledStyle : {}),
          ...(error ? dropZoneErrorStyle : {}),
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedTypes.join(',')}
          onChange={handleFileChange}
          style={hiddenInputStyle}
          disabled={disabled}
        />
        <span style={dropZoneTextStyle}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          {isDragOver ? dragActiveText : placeholder}
        </span>
      </label>
      {error && <div style={errorTextStyle}>{error}</div>}
    </div>
  );
};

/**
 * Styles
 */
const dropZoneStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  border: '2px dashed #d1d5db',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'border-color 0.15s, background-color 0.15s',
};

const dropZoneDragOverStyle: CSSProperties = {
  borderColor: '#3b82f6',
  backgroundColor: '#eff6ff',
};

const dropZoneDisabledStyle: CSSProperties = {
  cursor: 'not-allowed',
  opacity: 0.6,
};

const dropZoneErrorStyle: CSSProperties = {
  borderColor: '#ef4444',
};

const hiddenInputStyle: CSSProperties = {
  display: 'none',
};

const dropZoneTextStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '0.875rem',
  color: '#6b7280',
};

const previewContainerStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
};

const previewImageStyle: CSSProperties = {
  maxWidth: '100%',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
};

const removeButtonStyle: CSSProperties = {
  position: 'absolute',
  top: '8px',
  right: '8px',
  padding: '4px 8px',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'white',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};

const errorTextStyle: CSSProperties = {
  marginTop: '8px',
  fontSize: '0.875rem',
  color: '#ef4444',
};

export default ImageDropZone;
